/**
 * Maps AttributeDefinition[] (from attribute_master API) to FieldDefinition[]
 * (consumed by the form rendering system).
 *
 * Handles:
 *   - control_type → FieldType mapping
 *   - display_order sorting
 *   - Static options for known field name patterns (e.g. year ranges)
 *   - Sentinel options ['Select'] for API-driven selects,
 *     which triggers the existing ref-master augmentation pipeline
 */

import type { FieldDefinition, FieldType } from '@/types'
import type { AttributeDefinition } from '@/services/attributeService'

// ── control_type → FieldType ──────────────────────────────────────────────────

const CONTROL_TYPE_MAP: Record<string, FieldType> = {
  text:     'text',
  number:   'number',
  select:   'select',
  toggle:   'toggle',
  checkbox: 'checkbox',
  textarea: 'textarea',
}

// ── Static options registry ───────────────────────────────────────────────────

/**
 * Registry of attribute_name patterns that should use locally-computed options
 * instead of fetching from the ref_master API.
 *
 * Add new entries here when new static-option patterns are needed.
 * The `matches` predicate receives the full `attribute_name` string.
 */
interface StaticOptionEntry {
  matches: (attributeName: string) => boolean
  getOptions: () => string[]
}

const STATIC_OPTIONS_REGISTRY: StaticOptionEntry[] = [
  {
    // Any field named 'year' or ending with '_year' gets a computed year range
    matches: (name) => name === 'year' || name.endsWith('_year'),
    getOptions: () => [
      'Select Year',
      ...Array.from({ length: 30 }, (_, i) => String(2026 - i)),
    ],
  },
  // Add more entries here as required, for example:
  // {
  //   matches: (name) => name.endsWith('_month'),
  //   getOptions: () => ['Select Month', 'Jan', 'Feb', ...],
  // },
]

function getStaticOptions(attributeName: string): string[] | null {
  for (const entry of STATIC_OPTIONS_REGISTRY) {
    if (entry.matches(attributeName)) return entry.getOptions()
  }
  return null
}

// ── Mapper ────────────────────────────────────────────────────────────────────

/**
 * Converts an array of AttributeDefinition (from attribute_master) into
 * FieldDefinition[] ready for the form rendering pipeline.
 *
 * - Sorted by display_order ascending.
 * - Select fields with static option patterns get computed options.
 * - Select fields without static options get the sentinel ['Select'],
 *   which causes the existing augmentFieldOptions pipeline to replace them with
 *   live data fetched from the ref_master API.
 */
export function mapAttributesToFields(
  attributes: AttributeDefinition[]
): FieldDefinition[] {
  return [...attributes]
    .sort((a, b) => a.display_order - b.display_order)
    .map((attr) => {
      const fieldType: FieldType = CONTROL_TYPE_MAP[attr.control_type] ?? 'text'

      const field: FieldDefinition = {
        id:       attr.attribute_name,
        label:    attr.label,
        type:     fieldType,
        required: attr.is_required,
      }

      if (fieldType === 'select') {
        const staticOpts = getStaticOptions(attr.attribute_name)
        field.options = staticOpts ?? ['Select']
        if (!staticOpts && attr.ref_category) {
          field.refCategory = attr.ref_category
        }
      }

      return field
    })
}
